import { Box, Card, Text } from '@chakra-ui/react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { systemSettingsApi } from '~/apis/systemSettingsApi'
import { useUserState } from '~/atoms/UserState'
import { toaster } from '~/components/ui/toaster'
import { useCheckRole } from '~/hooks/useCheckRole'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { getLocalizedDeleteScheduleErrorMessage } from '~/pages/Schedules/scheduleDeleteErrorMessage'
import {
  getLocalizedScheduleQueryError,
  localizeScheduleQueryError,
} from '~/pages/Schedules/scheduleQueryErrorMessage'
import {
  SCHEDULE_QUERY_KEYS,
  useCreateSchedule,
  useDeleteSchedule,
  useListSchedules,
} from '~/queries/scheduleQueries'
import { isUserAdmin } from '~/utils/auth'
import { buildAssetScheduleCreatePayload } from '~/utils/scheduleApiClient/buildAssetScheduleCreatePayload'
import { useScheduleApi } from '~/utils/scheduleApiClient/scheduleApiClient'

import { normalizeTimeZone } from '@mangovoice/constants'
import {
  CreateScheduleModal,
  resolveScheduleCardMetaLeft,
  resolveScheduleCardMetaRight,
  resolveScheduleCardStatus,
  resolveScheduleListPbxScope,
  type Schedule,
  type ScheduleCardStatus,
  type ScheduleCreate,
  type ScheduleEvent,
  ScheduleI18nProvider,
  type ScheduleId,
  SchedulesListPageView,
  useScheduleListPageClamp,
  useScheduleListPagination,
  useScheduleListSearch,
  useScheduleListSummaries,
} from '@mangovoice/schedule'
import { useGetSystemSettings } from '@mangovoice/system-settings-ui'

const scheduleDetailPath = (
  scheduleId: ScheduleId,
  basePath: string,
): string => {
  return `${basePath}/${scheduleId}`
}

export const SchedulesListPage = () => {
  const { t, i18n } = useTranslation()
  const scheduleLocale = i18n.resolvedLanguage ?? i18n.language
  const { pbx, pbxId, userRoles } = useUserState()
  const checkRole = useCheckRole()
  const canDelete = checkRole('senior')
  const navigate = useNavigate()
  const schedulesBasePath = ApplicationRoutes.SCHEDULES
  const scheduleApi = useScheduleApi()
  const scheduleListPbxScope = resolveScheduleListPbxScope(pbxId, 'single-pbx')
  const isValidPbxId = scheduleListPbxScope.enabled
  const canCreateSchedule = isValidPbxId && Boolean(pbx?.uuid)
  const { data: systemSettings } = useGetSystemSettings(
    systemSettingsApi,
    pbxId,
  )
  const initialScheduleTimeZone = useMemo(() => {
    const defaultTimeZone = systemSettings?.default_time_zone
    return defaultTimeZone ? normalizeTimeZone(defaultTimeZone) : undefined
  }, [systemSettings?.default_time_zone])

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    effectiveLimit,
    effectiveOffset,
  } = useScheduleListPagination()
  const { searchInput, setSearchInput, debouncedSearch } =
    useScheduleListSearch({
      setCurrentPage,
    })

  const { schedules, isLoading, error, pagination } = useListSchedules({
    queryParams: {
      pbx_id: scheduleListPbxScope.pbxId,
      limit: effectiveLimit,
      offset: effectiveOffset,
      search: debouncedSearch.trim() || undefined,
    },
    options: {
      enabled: isValidPbxId,
    },
  })

  useScheduleListPageClamp({
    pageSize,
    currentPage,
    setCurrentPage,
    totalCount: pagination.count,
    isLoading,
    hasError: !!error,
  })

  const { scheduleSummariesById, loadingScheduleIds } =
    useScheduleListSummaries({
      schedules,
      getEventsQueryKey: (scheduleId) => SCHEDULE_QUERY_KEYS.events(scheduleId),
      fetchScheduleEvents: async (scheduleId): Promise<ScheduleEvent[]> => {
        const { ok, data } = await scheduleApi.getScheduleEvents(scheduleId)
        if (!ok || !data) return []
        return data
      },
      labels: {
        hoursVaryLabel: t('schedule.list.hoursVary', 'Hours vary'),
        unavailableLabel: t('schedule.list.unavailable', 'Unavailable'),
        weekdayLabels: {
          1: t('schedule.common.weekdayShort.mon', 'Mon'),
          2: t('schedule.common.weekdayShort.tue', 'Tue'),
          3: t('schedule.common.weekdayShort.wed', 'Wed'),
          4: t('schedule.common.weekdayShort.thu', 'Thu'),
          5: t('schedule.common.weekdayShort.fri', 'Fri'),
          6: t('schedule.common.weekdayShort.sat', 'Sat'),
          7: t('schedule.common.weekdayShort.sun', 'Sun'),
        },
      },
    })

  const createMutation = useCreateSchedule({
    options: {
      onSuccess: (newSchedule) => {
        toaster.create({
          title: t('schedule.list.toast.createSuccess', 'Schedule created'),
          description: t(
            'schedule.list.toast.createSuccessDescription',
            'The schedule has been created successfully.',
          ),
          type: 'success',
          duration: 5000,
          closable: true,
        })
        setIsCreateModalOpen(false)

        if (newSchedule) {
          navigate(scheduleDetailPath(newSchedule.id, schedulesBasePath))
        }
      },
      onError: (createError) => {
        toaster.create({
          title: t('schedule.list.toast.createFailed', 'Create failed'),
          description: getLocalizedScheduleQueryError({
            error: createError,
            fallbackMessage: t(
              'schedule.list.toast.createFailedDescription',
              'Failed to create schedule',
            ),
            t,
          }),
          type: 'error',
          duration: 5000,
          closable: true,
        })
      },
    },
  })

  const deleteMutation = useDeleteSchedule({
    options: {
      onSuccess: () => {
        toaster.create({
          title: t('schedule.list.toast.deleteSuccess', 'Schedule deleted'),
          description: t(
            'schedule.list.toast.deleteSuccessDescription',
            'The schedule has been deleted successfully.',
          ),
          type: 'success',
          duration: 5000,
          closable: true,
        })
      },
      onError: (deleteError) => {
        toaster.create({
          title: t('schedule.list.toast.deleteFailed', 'Delete failed'),
          description: getLocalizedDeleteScheduleErrorMessage({
            error: deleteError,
            fallbackMessage: t(
              'schedule.list.toast.deleteFailedDescription',
              'Failed to delete schedule',
            ),
            t,
          }),
          type: 'error',
          duration: 7000,
          closable: true,
        })
      },
    },
  })

  const handleCreate = (data: ScheduleCreate, defaultWeeklyHours: boolean) => {
    if (!pbx?.uuid) return

    createMutation.mutate({
      data: buildAssetScheduleCreatePayload(data, pbx.uuid),
      defaultWeeklyHours,
    })
  }

  const handleDelete = (schedule: Schedule) => {
    deleteMutation.mutate(schedule.id)
  }

  const getScheduleStatus = (schedule: Schedule): ScheduleCardStatus => {
    return resolveScheduleCardStatus(
      schedule,
      scheduleSummariesById.get(schedule.id),
      {
        openNowLabel: t('schedule.list.status.openNow', 'Open now'),
        holidayLabel: t('schedule.list.status.holiday', 'Holiday'),
        closedLabel: t('schedule.list.status.closed', 'Closed'),
      },
    )
  }

  const getMetaLeft = (schedule: Schedule): string => {
    return resolveScheduleCardMetaLeft(
      scheduleSummariesById.get(schedule.id),
      t('schedule.list.unavailable', 'Unavailable'),
    )
  }

  const getMetaRight = (schedule: Schedule): string => {
    return resolveScheduleCardMetaRight(
      scheduleSummariesById.get(schedule.id),
      t('schedule.list.unavailable', 'Unavailable'),
    )
  }

  const strings = useMemo(
    () => ({
      title: t('schedule.list.title', 'Schedules'),
      subtitle: t(
        'schedule.list.subtitle',
        'View and manage schedule configurations',
      ),
      newSchedule: t('schedule.list.newSchedule', 'New Schedule'),
      loading: t('schedule.list.loading', 'Loading schedules...'),
      emptyTitle: t('schedule.list.noSchedules', 'No Schedules found'),
      emptyDescription: t(
        'schedule.list.noSchedulesDescription',
        'Click on add new schedule to start adding your own availability',
      ),
      emptySearchTitle: t(
        'schedule.list.noSearchMatches',
        'No schedules match your search',
      ),
      emptySearchDescription: t(
        'schedule.list.noSearchMatchesDescription',
        'Try a different search term.',
      ),
      deleteSchedule: t('schedule.list.delete', 'Delete'),
    }),
    [t],
  )

  if (!isUserAdmin(userRoles)) {
    return (
      <ScheduleI18nProvider t={t} locale={scheduleLocale}>
        <Box p={6}>
          <Card.Root>
            <Card.Body>
              <Text color="fg.muted">
                {t(
                  'schedule.list.noPermission',
                  "You don't have permission to access schedules.",
                )}
              </Text>
            </Card.Body>
          </Card.Root>
        </Box>
      </ScheduleI18nProvider>
    )
  }

  return (
    <ScheduleI18nProvider t={t} locale={scheduleLocale}>
      <Box w="full" minW={0} h="full" minH={0}>
        <SchedulesListPageView
          schedules={schedules}
          isLoading={isLoading || !isValidPbxId}
          loadingScheduleIds={loadingScheduleIds}
          error={localizeScheduleQueryError(error, t)}
          strings={strings}
          canCreate={canCreateSchedule}
          onCreate={() => setIsCreateModalOpen(true)}
          onScheduleClick={(scheduleId) =>
            navigate(scheduleDetailPath(scheduleId, schedulesBasePath))
          }
          getScheduleHref={(scheduleId) =>
            scheduleDetailPath(scheduleId, schedulesBasePath)
          }
          onDeleteSchedule={canDelete ? handleDelete : undefined}
          getScheduleStatus={getScheduleStatus}
          getMetaLeft={getMetaLeft}
          getMetaRight={getMetaRight}
          totalCount={pagination.count}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
        />

        {isCreateModalOpen &&
          scheduleListPbxScope.pbxId !== undefined &&
          pbx?.uuid && (
            <CreateScheduleModal
              isOpen={isCreateModalOpen}
              onClose={() => setIsCreateModalOpen(false)}
              pbxId={scheduleListPbxScope.pbxId}
              onSave={handleCreate}
              isSaving={createMutation.isPending}
              t={t}
              initialTimeZone={initialScheduleTimeZone}
              onNotify={(notification) => {
                toaster.create({
                  title: notification.title,
                  description: notification.description,
                  type: notification.type,
                  duration: 3000,
                  closable: true,
                })
              }}
            />
          )}
      </Box>
    </ScheduleI18nProvider>
  )
}
